import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { teacherSubtopicDrilldown } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";

export default function TeacherSubtopicDrill() {
  const { learnerId, subject, subTopic } = useParams();
  const [data, setData] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    teacherSubtopicDrilldown(learnerId, decodeURIComponent(subject), decodeURIComponent(subTopic)).then(setData);
  }, [learnerId, subject, subTopic]);

  if (!data) return <div className="text-center py-20 text-slate-400" data-testid="drill-loading">Loading…</div>;

  const { learner, rows, summary, stops, skill_state } = data;

  return (
    <div className="teacher-scope min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-subtopic-drill">
        <Link to={`/teacher/student/${learnerId}`} className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline mb-4 tap-44" data-testid="back-student">
          <ArrowLeft className="w-4 h-4" /> Back to {learner?.name || "student"}
        </Link>

        <h1 className="font-teacher text-2xl md:text-3xl font-bold text-slate-900" data-testid="drill-headline">
          {decodeURIComponent(subject)} • <span className="text-slate-700">{decodeURIComponent(subTopic)}</span>
        </h1>
        <p className="text-slate-600 mt-1">{learner?.name}'s sub-topic drill-down</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6" data-testid="summary-cards">
          <Stat label="Total attempts" value={summary.total_attempts} />
          <Stat label="Correct" value={summary.correct} tone="emerald" />
          <Stat label="Foundational ✓" value={summary.foundational_correct} tone="sky" />
          <Stat label="Foundational ✕" value={summary.foundational_wrong} tone="amber" />
        </div>

        {skill_state && (
          <Card className="rounded-xl p-4 bg-white border border-slate-200 mb-6" data-testid="skill-state-card">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="text-slate-500">Pool A:</span><strong>{skill_state.pool_a_status}</strong>
              <span className="text-slate-500 ml-4">Pool B unlocked:</span><strong>{skill_state.pool_b_unlocked ? "Yes" : "No"}</strong>
              <span className="text-slate-500 ml-4">WarmUp flag:</span><strong>{skill_state.warmup_flag ? "weak" : "ok"}</strong>
            </div>
          </Card>
        )}

        {Object.keys(summary.errors_by_type).length > 0 && (
          <Card className="rounded-xl p-4 bg-amber-50 border border-amber-200 mb-6" data-testid="error-breakdown">
            <h3 className="font-teacher font-semibold text-amber-900 mb-2">Errors by type</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.errors_by_type).map(([k, v]) => (
                <span key={k} className="px-2 py-1 bg-white rounded text-xs font-semibold text-amber-800 border border-amber-200">
                  {k}: {v}
                </span>
              ))}
            </div>
          </Card>
        )}

        {stops.length > 0 && (
          <Card className="rounded-xl p-4 bg-white border border-amber-200 mb-6" data-testid="stops-card">
            <h3 className="font-teacher font-semibold text-slate-900 mb-2">Stop events ({stops.length})</h3>
            <div className="space-y-2">
              {stops.map(s => (
                <Link key={s.stop_id} to={`/teacher/alert/${s.stop_id}`} className="block bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded p-2 text-sm" data-testid={`stop-link-${s.stop_id}`}>
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-4 h-4" />
                    <span>Trigger layer: {s.trigger_layer} • Reroute: {s.reroute_type}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{new Date(s.created_at).toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        <h2 className="font-teacher text-xl font-semibold text-slate-900 mb-3">Per-question detail</h2>
        <div className="space-y-3" data-testid="rows-list">
          {rows.map((r, i) => {
            const a = r.attempt;
            const q = r.question;
            return (
              <Card key={i} className={`rounded-xl p-4 border ${a.is_correct ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/40"}`} data-testid={`row-${i}`}>
                <div className="flex items-start gap-3 mb-2">
                  {a.is_correct ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono text-slate-500">{a.record_id}</span>
                      <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700">{a.layer}</span>
                      {a.question_pool && <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800">{a.question_pool}</span>}
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{a.difficulty}</span>
                      {a.diagnostic_signal === "foundational" && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Foundational gate</span>}
                      {a.error_type && !a.is_correct && <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800">Err: {a.error_type}</span>}
                    </div>
                    {q?.question_text && <p className="text-slate-800 mt-2">{q.question_text}</p>}
                    <div className="text-sm mt-2">
                      <span className="text-slate-500">Answered:</span> <strong>{a.answer_given}</strong>
                      {!a.is_correct && q && <> • <span className="text-slate-500">correct:</span> <strong className="text-emerald-700">{q.correct_answer_text || q.correct_answer}</strong></>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">{Math.round(a.time_taken_sec)}s</div>
                </div>
              </Card>
            );
          })}
          {rows.length === 0 && <div className="text-center text-slate-400 py-8" data-testid="rows-empty">No attempts yet on this sub-topic.</div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-900 bg-slate-50",
    emerald: "text-emerald-700 bg-emerald-50",
    sky: "text-sky-700 bg-sky-50",
    amber: "text-amber-700 bg-amber-50",
  };
  return (
    <div className={`rounded-xl p-3 ${toneMap[tone]}`}>
      <div className="text-xs uppercase font-semibold text-slate-500">{label}</div>
      <div className="font-teacher text-2xl font-bold">{value}</div>
    </div>
  );
}
